// Pure normalisation for the causal-run response body (ledger §C item 3, contract 13 §14).
// Framework-free: no Vue, no store, no `shared/http`.

/**
 * The causal model can emit bare `NaN`/`Infinity`/`-Infinity` for an under-determined effect
 * estimate — none of those are valid JSON, so `JSON.parse` throws on them. The pattern is
 * ANCHORED to the `key:` side of a pair on purpose (mirrors `commerce-analytics`'s
 * `sanitizeNaN`, the one other measured instance of this exact backend quirk in this codebase):
 *
 *   `replaceAll('NaN', '0')` would rewrite any string VALUE containing "NaN" — a campaign
 *   literally named "NaN Campaign" becomes "0 Campaign". `/:\s*NaN\b/g → ': 0'` only touches a
 *   value position.
 *
 * Contract §14: a malformed numeric becomes `0` here (not `null`, unlike the commerce-analytics
 * sibling) — a causal effect that couldn't be computed reads as "no measured effect", which is
 * this module's own display convention throughout (`causalOutputMapper`'s `?? 0` fallbacks).
 */
export function sanitizeCausalNumbers(raw: string): string {
  return raw.replace(/:\s*(-?Infinity|NaN)\b/gu, ': 0')
}

/**
 * `transformResponse` entry point: sanitise, then parse. A body that is still not JSON after
 * sanitising is returned VERBATIM so the failure surfaces at decode time as a typed `ApiError`
 * rather than being silently swallowed.
 */
export function parseCausalBody(raw: unknown): unknown {
  if (typeof raw !== 'string') return raw
  try {
    return JSON.parse(sanitizeCausalNumbers(raw))
  } catch {
    return raw
  }
}
